import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk, Upload, Download, ClockCounterClockwise, Image as ImageIcon, X } from '@phosphor-icons/react';

const SIMPLE_MASTERS = [
  { slug: 'categories',    label: 'Categories',     hint: 'Windows, Doors, Partitions…' },
  { slug: 'profile-types', label: 'Profile Types',  hint: '60mm, 88mm, 60mm Sliding…' },
  { slug: 'openings',      label: 'Openings',       hint: 'Sliding, Casement, Fixed…' },
  { slug: 'room-types',    label: 'Room Types',     hint: 'Bedroom, Kitchen, Hall…' },
];

const RATED_MASTERS = [
  { slug: 'glass-types', label: 'Glass Type Master', hint: 'e.g. Clear 5mm, Tinted 5mm' },
  { slug: 'mesh-types',  label: 'Mesh Type Master',  hint: 'e.g. SS, Aluminium' },
];

function todayISO() { return new Date().toISOString().split('T')[0]; }

// ===== Item + Price Master =====
function QmItemMaster({ onShowHistory }) {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [imgUploadingKey, setImgUploadingKey] = useState(null);
  const [imgCacheBust, setImgCacheBust] = useState(0);
  const fileInputRef = useRef(null);
  const imgInputRef = useRef(null);
  const idRef = useRef(0);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, catRes] = await Promise.all([
        api.get('/api/quotation-masters/items'),
        api.get('/api/quotation-masters/categories'),
      ]);
      setRows((itemsRes.data || []).map(d => ({ ...d, _rowId: `srv-${idRef.current++}`, _saved: true, _originalName: d.item_name })));
      setCategories((catRes.data || []).map(c => c.name));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Failed to load items');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch_(); }, [fetch_]);

  const addRow = () => {
    const newRow = { _rowId: `new-${idRef.current++}`, item_name: '', category: '', price_per_sqft: 0, effective_date: todayISO(), description: '', is_active: true, _saved: false };
    setRows(prev => [...prev, newRow]);
  };

  const updateRow = (rowId, patch) => setRows(prev => prev.map(r => r._rowId === rowId ? { ...r, ...patch } : r));

  const saveRow = async (row) => {
    const newName = (row.item_name || '').trim();
    if (!newName) { toast.error('Item name is required'); return; }
    const price = Number(row.price_per_sqft);
    if (Number.isNaN(price) || price < 0) { toast.error('Price must be a non-negative number'); return; }
    setSaving(true);
    try {
      // If this row is already saved and the name changed, call the rename endpoint first.
      const originalName = row._originalName || '';
      const isRename = row._saved && originalName && originalName !== newName;
      if (isRename) {
        const ok = window.confirm(
          `Rename item "${originalName}" to "${newName}"?\n\n` +
          `• Price, category, image and rate history are preserved.\n` +
          `• Existing quotations were snapshotted with the old name and will NOT be rewritten.`
        );
        if (!ok) { setSaving(false); return; }
        await api.patch(
          `/api/quotation-masters/items/${encodeURIComponent(originalName)}/rename`,
          { new_name: newName },
        );
      }
      const { data } = await api.post('/api/quotation-masters/items', {
        item_name: newName,
        price_per_sqft: price,
        category: (row.category || '').trim(),
        effective_date: row.effective_date || todayISO(),
        description: row.description || '',
        is_active: row.is_active !== false,
      });
      toast.success(isRename ? `Renamed and saved "${newName}"` : (data?.message || 'Saved'));
      fetch_();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const removeRow = async (row) => {
    if (!row._saved) { setRows(prev => prev.filter(r => r._rowId !== row._rowId)); return; }
    if (!window.confirm(`Delete item "${row.item_name}"? Existing quotations are not affected.`)) return;
    try {
      await api.delete(`/api/quotation-masters/items/${encodeURIComponent(row.item_name)}`);
      toast.success('Deleted');
      fetch_();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.xlsx') && !ext.endsWith('.csv') && !ext.endsWith('.xlsm')) {
      toast.error('Only .xlsx or .csv files'); e.target.value = ''; return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/api/quotation-masters/items/upload-excel', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
      });
      toast.success(data?.message || 'Imported');
      if (data?.errors?.length) {
        toast.error(`${data.errors.length} row(s) had issues — first: ${data.errors[0]}`, { duration: 8000 });
      }
      fetch_();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = 'item_name,category,price_per_sqft,description\n2 Track Sliding Window,Windows,450,Standard\nCasement Door,Doors,750,\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'qm_items_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const exportItems = async (fmt) => {
    setExporting(true);
    try {
      const response = await api.get(`/api/quotation-masters/items/export?format=${fmt}`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().split('T')[0];
      a.download = `qm_items_${stamp}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} item(s) as ${fmt.toUpperCase()}`);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  // Image upload / remove for a specific item row
  const uploadItemImage = async (row, file) => {
    if (!row.item_name || !row._saved) {
      toast.error('Save the item first, then attach an image');
      return;
    }
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large (max 5 MB)');
      return;
    }
    setImgUploadingKey(row._rowId);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post(`/api/quotation-masters/items/${encodeURIComponent(row.item_name)}/image`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Image uploaded for "${row.item_name}"`);
      // Optimistically mark row as having an image (for badge) + bust cache
      setRows(prev => prev.map(x => x._rowId === row._rowId ? { ...x, image_path: 'uploaded' } : x));
      setImgCacheBust(t => t + 1);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setImgUploadingKey(null);
    }
  };

  const removeItemImage = async (row) => {
    if (!row.item_name || !row._saved) return;
    if (!window.confirm(`Remove image from "${row.item_name}"?`)) return;
    try {
      await api.delete(`/api/quotation-masters/items/${encodeURIComponent(row.item_name)}/image`);
      toast.success('Image removed');
      setRows(prev => prev.map(x => x._rowId === row._rowId ? { ...x, image_path: null } : x));
      setImgCacheBust(t => t + 1);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err.response?.data?.detail || 'Failed to remove image');
    }
  };

  return (
    <Card className="rounded-sm shadow-sm border-zinc-300">
      <CardHeader className="border-b border-zinc-300 flex flex-row items-center justify-between flex-wrap gap-3">
        <CardTitle className="text-xl font-bold text-zinc-950">
          Item & Price Master
          <Badge className="ml-2 bg-emerald-100 text-emerald-800 border border-emerald-200">₹ per Sqft</Badge>
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="qm-item-template" variant="outline" onClick={downloadTemplate} className="h-9">
            <Download size={16} weight="bold" className="mr-1.5" /> Template
          </Button>
          <Button
            data-testid="qm-item-export-csv"
            variant="outline"
            onClick={() => exportItems('csv')}
            disabled={exporting || rows.length === 0}
            className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Download size={16} weight="bold" className="mr-1.5" /> Export CSV
          </Button>
          <Button
            data-testid="qm-item-export-xlsx"
            variant="outline"
            onClick={() => exportItems('xlsx')}
            disabled={exporting || rows.length === 0}
            className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Download size={16} weight="bold" className="mr-1.5" /> Export XLSX
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xlsm,.csv"
            data-testid="qm-item-upload-input"
            className="hidden"
            onChange={handleUpload}
          />
          <Button
            data-testid="qm-item-upload"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="h-9 border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Upload size={16} weight="bold" className="mr-1.5" /> {uploading ? 'Uploading…' : 'Import Excel/CSV'}
          </Button>
          <Button data-testid="qm-item-add" onClick={addRow} className="h-9 bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={16} weight="bold" className="mr-1.5" /> Add Item
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-zinc-400">Loading items…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-zinc-400">No items yet. Click <strong>Add Item</strong> or upload a CSV.</div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="qm-item-table" className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Item Name *</th>
                  <th className="text-center p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-24">Image</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-40">Category</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-32">Price ₹/sqft *</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-36">Effective Date</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Description</th>
                  <th className="text-center p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-36">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r._rowId} className="border-b border-zinc-100">
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-item-name-${r._rowId}`}
                        value={r.item_name || ''}
                        onChange={e => updateRow(r._rowId, { item_name: e.target.value })}
                        placeholder="e.g. 2 Track Sliding Window"
                        className={`h-9 text-sm ${r._saved && r._originalName && r._originalName !== (r.item_name || '') ? 'border-amber-400 bg-amber-50' : ''}`}
                        title={r._saved ? 'Editing the name will rename this master (existing quotations keep the old name in their snapshot).' : ''}
                      />
                    </td>
                    <td className="p-1.5 text-center">
                      {r._saved ? (
                        <div className="relative inline-block group">
                          {r.image_path ? (
                            <div className="relative w-14 h-14 border border-zinc-300 rounded overflow-hidden bg-zinc-50">
                              <img
                                key={`${r._rowId}-${imgCacheBust}`}
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/quotation-masters/items/${encodeURIComponent(r.item_name)}/image?v=${imgCacheBust}`}
                                alt={r.item_name}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />
                              <button
                                onClick={() => removeItemImage(r)}
                                title="Remove image"
                                data-testid={`qm-item-image-remove-${r._rowId}`}
                                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X size={10} weight="bold" />
                              </button>
                            </div>
                          ) : (
                            <label
                              className="w-14 h-14 border-2 border-dashed border-zinc-300 rounded flex items-center justify-center cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors"
                              data-testid={`qm-item-image-upload-${r._rowId}`}
                              title="Upload image"
                            >
                              {imgUploadingKey === r._rowId ? (
                                <div className="w-3 h-3 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <ImageIcon size={18} className="text-zinc-400" />
                              )}
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) uploadItemImage(r, f);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          )}
                          {r.image_path && (
                            <label
                              className="absolute inset-0 opacity-0 hover:opacity-100 bg-black/40 rounded flex items-center justify-center cursor-pointer transition-opacity"
                              title="Replace image"
                            >
                              <Upload size={14} className="text-white" weight="bold" />
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) uploadItemImage(r, f);
                                  e.target.value = '';
                                }}
                              />
                            </label>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-zinc-400">Save row first</span>
                      )}
                    </td>
                    <td className="p-1.5">
                      <input
                        data-testid={`qm-item-category-${r._rowId}`}
                        list={`qm-category-options-${r._rowId}`}
                        value={r.category || ''}
                        onChange={e => updateRow(r._rowId, { category: e.target.value })}
                        placeholder="Pick or type"
                        className="h-9 w-full px-2 text-sm border border-zinc-300 rounded bg-white"
                      />
                      <datalist id={`qm-category-options-${r._rowId}`}>
                        {categories.map(c => <option key={c} value={c} />)}
                      </datalist>
                    </td>
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-item-price-${r._rowId}`}
                        type="number"
                        step="0.01"
                        min="0"
                        value={r.price_per_sqft ?? 0}
                        onChange={e => updateRow(r._rowId, { price_per_sqft: e.target.value })}
                        className="h-9 text-sm font-bold"
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-item-effective-${r._rowId}`}
                        type="date"
                        value={r.effective_date || ''}
                        onChange={e => updateRow(r._rowId, { effective_date: e.target.value })}
                        className="h-9 text-sm"
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-item-desc-${r._rowId}`}
                        value={r.description || ''}
                        onChange={e => updateRow(r._rowId, { description: e.target.value })}
                        placeholder="Optional"
                        className="h-9 text-sm"
                      />
                    </td>
                    <td className="p-1.5 text-center">
                      <div className="flex justify-center gap-1">
                        <Button
                          data-testid={`qm-item-save-${r._rowId}`}
                          onClick={() => saveRow(r)}
                          size="sm"
                          disabled={saving}
                          className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <FloppyDisk size={14} weight="bold" />
                        </Button>
                        {r._saved && (
                          <Button
                            data-testid={`qm-item-history-${r._rowId}`}
                            onClick={() => onShowHistory && onShowHistory('items', r.item_name)}
                            variant="outline"
                            size="sm"
                            className="h-8 border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                            title="Rate history"
                          >
                            <ClockCounterClockwise size={14} weight="bold" />
                          </Button>
                        )}
                        <Button
                          data-testid={`qm-item-delete-${r._rowId}`}
                          onClick={() => removeRow(r)}
                          variant="outline"
                          size="sm"
                          className="h-8 border-red-300 text-red-700 hover:bg-red-50"
                        >
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="px-4 py-2 text-xs text-zinc-500 bg-amber-50 border-t border-amber-100">
          ℹ Changes to prices here do <strong>not</strong> affect any existing quotations — prices are snapshotted at quotation save time.
        </p>
      </CardContent>
    </Card>
  );
}

// ===== Simple name-only masters (5 of them) =====
function QmSimpleMaster({ slug, label, hint }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/quotation-masters/${slug}`);
      setRows(data || []);
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(`Failed to load ${label}`);
    } finally { setLoading(false); }
  }, [slug, label]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const add = async () => {
    const name = newName.trim();
    if (!name) { toast.error(`${label.slice(0, -1)} name is required`); return; }
    setSaving(true);
    try {
      await api.post(`/api/quotation-masters/${slug}`, { name, description: '', is_active: true });
      toast.success('Added');
      setNewName('');
      fetch_();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/api/quotation-masters/${slug}/${encodeURIComponent(name)}`);
      toast.success('Deleted');
      fetch_();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card className="rounded-sm shadow-sm border-zinc-300">
      <CardHeader className="border-b border-zinc-300 py-3">
        <CardTitle className="text-base font-bold text-zinc-950">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 space-y-2">
        <div className="flex gap-2">
          <Input
            data-testid={`qm-${slug}-input`}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') add(); }}
            placeholder={`e.g. ${hint}`}
            className="h-9 text-sm"
          />
          <Button
            data-testid={`qm-${slug}-add`}
            onClick={add}
            disabled={saving || !newName.trim()}
            size="sm"
            className="h-9 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus size={14} weight="bold" className="mr-1" /> Add
          </Button>
        </div>
        {loading ? (
          <p className="p-3 text-sm text-zinc-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-3 text-sm text-zinc-400">None yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {rows.map(r => (
              <div
                key={r.name}
                data-testid={`qm-${slug}-chip-${r.name}`}
                className="flex items-center gap-1.5 bg-zinc-100 border border-zinc-200 rounded-full px-3 py-1 text-sm"
              >
                <span className="font-bold text-zinc-800">{r.name}</span>
                <button
                  data-testid={`qm-${slug}-remove-${r.name}`}
                  onClick={() => remove(r.name)}
                  className="text-zinc-400 hover:text-red-600"
                  title="Delete"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ===== Rated Master (Glass / Mesh) — table with rate, effective date, history =====
function QmRatedMaster({ slug, label, hint, onShowHistory }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const idRef = useRef(0);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/quotation-masters/rated/${slug}`);
      setRows((data || []).map(d => ({ ...d, _rowId: `srv-${idRef.current++}`, _saved: true })));
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(`Failed to load ${label}`);
    } finally { setLoading(false); }
  }, [slug, label]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const addRow = () => {
    const newRow = { _rowId: `new-${idRef.current++}`, name: '', category: '', rate_per_sqft: 0, effective_date: todayISO(), description: '', is_active: true, _saved: false };
    setRows(prev => [...prev, newRow]);
  };
  const updateRow = (rowId, patch) => setRows(prev => prev.map(r => r._rowId === rowId ? { ...r, ...patch } : r));

  const saveRow = async (row) => {
    if (!row.name?.trim()) { toast.error('Name is required'); return; }
    const rate = Number(row.rate_per_sqft);
    if (Number.isNaN(rate) || rate < 0) { toast.error('Rate must be a non-negative number'); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/api/quotation-masters/rated/${slug}`, {
        name: row.name.trim(),
        rate_per_sqft: rate,
        category: (row.category || '').trim(),
        effective_date: row.effective_date || todayISO(),
        description: row.description || '',
        is_active: row.is_active !== false,
      });
      toast.success(data?.message || 'Saved');
      fetch_();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const removeRow = async (row) => {
    if (!row._saved) { setRows(prev => prev.filter(r => r._rowId !== row._rowId)); return; }
    if (!window.confirm(`Delete "${row.name}"?\n\nThe record + full rate history will be archived in qm_archive (not lost).`)) return;
    try {
      await api.delete(`/api/quotation-masters/rated/${slug}/${encodeURIComponent(row.name)}`);
      toast.success('Deleted (archived)');
      fetch_();
    } catch (err) {
      if (err?.isAuthError) return;
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <Card data-testid={`qm-rated-${slug}`} className="rounded-sm shadow-sm border-zinc-300">
      <CardHeader className="border-b border-zinc-300 flex flex-row items-center justify-between flex-wrap gap-3 py-3">
        <CardTitle className="text-lg font-bold text-zinc-950 flex items-center gap-2">
          {label}
          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">₹ per Sqft</Badge>
        </CardTitle>
        <Button data-testid={`qm-rated-${slug}-add`} onClick={addRow} size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white">
          <Plus size={14} weight="bold" className="mr-1" /> Add
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-center text-zinc-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-zinc-400 text-sm">No entries. Click <strong>Add</strong> to create one. <em>{hint}</em></div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid={`qm-rated-${slug}-table`} className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Name *</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-32">Category</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-28">Rate ₹/sqft *</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-32">Effective</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-20">Active</th>
                  <th className="text-left p-2 text-xs font-bold uppercase tracking-wider text-zinc-500">Remarks</th>
                  <th className="text-center p-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-36">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r._rowId} className="border-b border-zinc-100">
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-rated-${slug}-name-${r._rowId}`}
                        value={r.name || ''}
                        onChange={e => updateRow(r._rowId, { name: e.target.value })}
                        disabled={r._saved}
                        placeholder={hint}
                        className="h-9 text-sm"
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-rated-${slug}-cat-${r._rowId}`}
                        value={r.category || ''}
                        onChange={e => updateRow(r._rowId, { category: e.target.value })}
                        placeholder="Optional"
                        className="h-9 text-sm"
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-rated-${slug}-rate-${r._rowId}`}
                        type="number" step="0.01" min="0"
                        value={r.rate_per_sqft ?? 0}
                        onChange={e => updateRow(r._rowId, { rate_per_sqft: e.target.value })}
                        className="h-9 text-sm font-bold"
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-rated-${slug}-eff-${r._rowId}`}
                        type="date"
                        value={r.effective_date || ''}
                        onChange={e => updateRow(r._rowId, { effective_date: e.target.value })}
                        className="h-9 text-sm"
                      />
                    </td>
                    <td className="p-1.5">
                      <Switch
                        data-testid={`qm-rated-${slug}-active-${r._rowId}`}
                        checked={r.is_active !== false}
                        onCheckedChange={v => updateRow(r._rowId, { is_active: v })}
                      />
                    </td>
                    <td className="p-1.5">
                      <Input
                        data-testid={`qm-rated-${slug}-desc-${r._rowId}`}
                        value={r.description || ''}
                        onChange={e => updateRow(r._rowId, { description: e.target.value })}
                        placeholder="Optional"
                        className="h-9 text-sm"
                      />
                    </td>
                    <td className="p-1.5 text-center">
                      <div className="flex justify-center gap-1">
                        <Button
                          data-testid={`qm-rated-${slug}-save-${r._rowId}`}
                          onClick={() => saveRow(r)}
                          size="sm"
                          disabled={saving}
                          className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <FloppyDisk size={14} weight="bold" />
                        </Button>
                        {r._saved && (
                          <Button
                            data-testid={`qm-rated-${slug}-history-${r._rowId}`}
                            onClick={() => onShowHistory && onShowHistory(slug, r.name)}
                            variant="outline"
                            size="sm"
                            className="h-8 border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                            title="Rate history"
                          >
                            <ClockCounterClockwise size={14} weight="bold" />
                          </Button>
                        )}
                        <Button
                          data-testid={`qm-rated-${slug}-delete-${r._rowId}`}
                          onClick={() => removeRow(r)}
                          variant="outline"
                          size="sm"
                          className="h-8 border-red-300 text-red-700 hover:bg-red-50"
                        >
                          <Trash size={14} weight="bold" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="px-3 py-2 text-xs text-zinc-500 bg-amber-50 border-t border-amber-100">
          ℹ Rate changes are tracked in history; existing quotations and job orders are unaffected.
        </p>
      </CardContent>
    </Card>
  );
}

// ===== Rate History Dialog =====
function RateHistoryDialog({ open, onClose, target }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !target) { setData(null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = target.kind === 'items'
          ? `/api/quotation-masters/items/${encodeURIComponent(target.name)}/history`
          : `/api/quotation-masters/rated/${target.kind}/${encodeURIComponent(target.name)}/history`;
        const res = await api.get(url);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!err?.isAuthError) toast.error('Failed to load history');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, target]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle>Rate History — {target?.name}</DialogTitle>
          <DialogDescription className="sr-only">Past rates and effective windows</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-zinc-400">Loading…</p>
        ) : data ? (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
              <span className="font-bold text-emerald-700 uppercase text-xs tracking-wider">Current</span>
              <p className="mt-1">
                ₹<span className="font-bold text-lg text-zinc-900">{Number(data.current_rate ?? 0).toLocaleString('en-IN')}</span> /sqft
                <span className="text-xs text-zinc-500 ml-3">since {data.current_effective_date || '—'}</span>
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Previous rates ({data.rate_history?.length || 0})</p>
              {(!data.rate_history || data.rate_history.length === 0) ? (
                <p className="text-sm text-zinc-400 italic">No previous rates. This is the first one.</p>
              ) : (
                <table className="w-full text-sm border border-zinc-200">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="text-left p-2 text-xs font-bold uppercase text-zinc-500">Rate ₹/sqft</th>
                      <th className="text-left p-2 text-xs font-bold uppercase text-zinc-500">From</th>
                      <th className="text-left p-2 text-xs font-bold uppercase text-zinc-500">To</th>
                      <th className="text-left p-2 text-xs font-bold uppercase text-zinc-500">Changed by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rate_history.slice().reverse().map((h, i) => (
                      <tr key={i} className="border-t border-zinc-100">
                        <td className="p-2 font-bold">₹{Number(h.rate ?? 0).toLocaleString('en-IN')}</td>
                        <td className="p-2 text-zinc-700">{h.from || '—'}</td>
                        <td className="p-2 text-zinc-700">{h.to || '—'}</td>
                        <td className="p-2 text-zinc-600">{h.changed_by || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          <p className="text-zinc-400">No data.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function QuotationMasterTab() {
  const [historyTarget, setHistoryTarget] = useState(null); // { kind, name }
  const showHistory = (kind, name) => setHistoryTarget({ kind, name });
  return (
    <div className="space-y-6">
      <QmItemMaster onShowHistory={showHistory} />
      <div className="space-y-4">
        {RATED_MASTERS.map(m => (
          <QmRatedMaster key={m.slug} slug={m.slug} label={m.label} hint={m.hint} onShowHistory={showHistory} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {SIMPLE_MASTERS.map(m => (
          <QmSimpleMaster key={m.slug} slug={m.slug} label={m.label} hint={m.hint} />
        ))}
      </div>
      <RateHistoryDialog
        open={!!historyTarget}
        target={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
    </div>
  );
}
